/**
 * Next-month planning maths (web copy of the phone lib/plan.js — keep both in step) (#392 / #393 / #394) — pure functions, no React,
 * so I can prove the numbers with plain node assertions.
 *
 * I take the server's month plan (GET /budget/months/<id>/plan/) and answer two
 * questions per bank and in total:
 *   1. The PLAN: money in (carried + new income + transfers in), planned out
 *      (expenses + transfers out) and the leftover the server worked out.
 *   2. The SIMULATION: "in the bank if these happen" — carried plus only the
 *      lines the user ticked. A ticked income with from_bank is a TRANSFER: it
 *      moves money from from_bank into bank and is never new money, so it nets
 *      to zero in the total. Nothing here is ever saved.
 *
 * I work in whole cents so R0.10 + R0.20 never drifts into R0.30000000004.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */

/** Rands (string or number) → integer cents; blanks and junk count as 0. */
export function toCents(v) {
  const n = Number(v)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

/** Integer cents → Rands as a number (for money() formatting). */
export function fromCents(c) {
  return c / 100
}

// A bank key that survives null (a line with no bank still counts in the total).
const keyOf = (id) => (id == null ? 'none' : String(id))

/**
 * The plan table: per bank carried / money in / planned out / leftover, plus the total
 * row. I read the server's `planned` rows (it owns the rule) and only add the
 * derived in/out columns; the total row uses the server's `totals`, where
 * transfers cancel out because they only move money between my own banks.
 */
export function plannedRows(plan) {
  const rows = (plan?.planned || []).map((p) => {
    const moneyIn = toCents(p.carried) + toCents(p.income) + toCents(p.transfers_in)
    const out = toCents(p.expenses) + toCents(p.transfers_out)
    return {
      bank: p.bank,
      bank_name: p.bank_name,
      // Opening balance brought forward from last month (#624), shown on its
      // own so the owner can see where "In" starts.
      carried: fromCents(toCents(p.carried)),
      moneyIn: fromCents(moneyIn),
      plannedOut: fromCents(out),
      leftover: fromCents(toCents(p.leftover)),
    }
  })
  const t = plan?.totals || {}
  const total = {
    carried: fromCents(toCents(t.carried)),
    moneyIn: fromCents(toCents(t.carried) + toCents(t.income)),
    plannedOut: fromCents(toCents(t.expenses)),
    leftover: fromCents(toCents(t.leftover)),
  }
  return { rows, total }
}

/**
 * The simulation: what would be in each bank if ONLY the ticked lines happen.
 *
 * plan   — the server plan ({banks:[{bank,bank_name,carried}], items:[...]})
 * ticked — a Set of item ids the user ticked (received / paid in the sim)
 *
 * Returns { banks: [{bank, bank_name, carried, simulated}], total } in Rands.
 * Per bank: carried + ticked income into it (non-transfer) + ticked transfers
 * into it − ticked transfers out of it − ticked expenses on it.
 */
export function computePlan(plan, ticked) {
  const on = ticked instanceof Set ? ticked : new Set(ticked || [])
  const order = []
  const byBank = new Map()
  // Names from either list, so a bank that only appears on a line still reads right.
  const names = new Map()
  for (const b of [...(plan?.planned || []), ...(plan?.banks || [])]) names.set(keyOf(b.bank), b.bank_name)
  const ensure = (id) => {
    const k = keyOf(id)
    if (!byBank.has(k)) {
      byBank.set(k, { bank: id ?? null, bank_name: names.get(k) || (id == null ? 'No bank' : 'Bank'), carried: 0, sim: 0 })
      order.push(k)
    }
    return byBank.get(k)
  }
  for (const b of plan?.banks || []) {
    const row = ensure(b.bank)
    row.carried += toCents(b.carried)
    row.sim += toCents(b.carried)
  }
  for (const it of plan?.items || []) {
    if (!on.has(it.id)) continue
    const amt = toCents(it.amount)
    if (it.kind === 'income') {
      ensure(it.bank).sim += amt
      // A transfer only MOVES money: whatever lands in `bank` leaves from_bank.
      if (it.from_bank != null) ensure(it.from_bank).sim -= amt
    } else if (it.kind === 'expense') {
      ensure(it.bank).sim -= amt
    }
  }
  const banks = order.map((k) => {
    const r = byBank.get(k)
    return { bank: r.bank, bank_name: r.bank_name, carried: fromCents(r.carried), simulated: fromCents(r.sim) }
  })
  const total = fromCents(order.reduce((s, k) => s + byBank.get(k).sim, 0))
  return { banks, total }
}

/** The starting tick set: lines already completed count as happened. */
export function initialTicks(plan) {
  return new Set((plan?.items || []).filter((i) => i.completed).map((i) => i.id))
}
